import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getDataTickers, getDataSummary, refreshDataCache, getDataSectors, getDataMarketStats, getHourlyProgress } from '../api';
import TickerDetail from './TickerDetail';
import { usePersistentPageSize } from '../hooks/usePersistentPageSize';

// ── Light theme ────────────────────────────────────────────────────────────
const C = {
  pageBg:    '#F1F5F9',
  cardBg:    '#FFFFFF',
  border:    '#E2E8F0',
  borderMd:  '#CBD5E1',
  text:      '#0F172A',
  textMd:    '#475569',
  textSm:    '#94A3B8',
  primary:   '#2563EB',
  primaryLt: '#EFF6FF',
  success:   '#16A34A',
  danger:    '#DC2626',
  warning:   '#D97706',
  shadow:    '0 1px 3px rgba(0,0,0,0.08)',
  shadowMd:  '0 4px 12px rgba(0,0,0,0.10)',
};

const S = {
  page:       { background: C.pageBg, minHeight: '100vh', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", color: C.text },
  wrap:       { maxWidth: 1600, margin: '0 auto', padding: '24px 28px' },
  statsGrid:  { display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 20 },
  statsCard:  { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px', boxShadow: C.shadow },
  statsTitle: { fontSize: 11, fontWeight: 700, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 },
  statsRow:   { display: 'flex', gap: 20, flexWrap: 'wrap' },
  statItem:   { flex: 1, minWidth: 100 },
  statLabel:  { fontSize: 11, color: C.textSm, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 },
  statValue:  { fontSize: 20, fontWeight: 700, color: C.text },
  statSub:    { fontSize: 12, color: C.textMd, marginTop: 1 },
  filterBar:  { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 },
  input:      {
    padding: '7px 11px', border: `1px solid ${C.border}`, borderRadius: 7,
    fontSize: 13, color: C.text, background: C.cardBg, outline: 'none', width: 220,
  },
  select:     {
    padding: '7px 11px', border: `1px solid ${C.border}`, borderRadius: 7,
    fontSize: 13, color: C.text, background: C.cardBg, cursor: 'pointer',
  },
  filterLabel:{ fontSize: 12, color: C.textMd, marginRight: 2 },
  card:       { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: C.shadow, overflow: 'hidden' },
  tableWrap:  { overflowX: 'auto' },
  table:      { borderCollapse: 'collapse', width: '100%', fontSize: 12 },
  th: (clickable, accent) => ({
    padding: '9px 10px',
    background: accent === 'hourly' ? '#EFF6FF' : accent === 'daily' ? '#F0FDF4' : accent === '5m' ? '#FEF9C3' : accent === '1m' ? '#FEE2E2' : '#F8FAFC',
    border: `1px solid ${C.border}`,
    color: accent === 'hourly' ? '#1D4ED8' : accent === 'daily' ? '#15803D' : accent === '5m' ? '#92400E' : accent === '1m' ? '#991B1B' : C.textMd,
    fontWeight: 600, textAlign: 'left', whiteSpace: 'nowrap',
    position: 'sticky', top: 0, zIndex: 1,
    cursor: clickable ? 'pointer' : 'default', userSelect: 'none',
  }),
  td:    { padding: '7px 10px', border: `1px solid ${C.border}`, color: C.text, whiteSpace: 'nowrap', verticalAlign: 'middle' },
  tdNum: { padding: '7px 10px', border: `1px solid ${C.border}`, color: C.text, whiteSpace: 'nowrap', verticalAlign: 'middle', textAlign: 'right' },
  pagRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderTop: `1px solid ${C.border}`, background: C.cardBg },
  btnPage: (active) => ({
    padding: '4px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer', border: `1px solid ${C.border}`,
    background: active ? C.primary : C.cardBg, color: active ? '#fff' : C.text,
    fontWeight: active ? 600 : 400,
  }),
  badge: (color) => ({
    display: 'inline-block', padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600,
    background: color === 'green' ? '#DCFCE7' : color === 'orange' ? '#FEF3C7' : '#F1F5F9',
    color: color === 'green' ? C.success : color === 'orange' ? C.warning : C.textSm,
  }),
  rowAlt: { background: '#FAFBFC' },
  switcherWrap: { display: 'flex', gap: 0, marginBottom: 16, borderRadius: 8, overflow: 'hidden', border: `1px solid ${C.border}`, width: 'fit-content' },
  switcherBtn: (active) => ({
    padding: '8px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
    background: active ? C.primary : C.cardBg,
    color: active ? '#fff' : C.textMd,
    borderRight: `1px solid ${C.border}`,
  }),
  alertBanner: {
    background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8,
    padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12,
    fontSize: 13, color: '#92400E',
  },
};

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250];

// Статуси з кешу: 'ok' / 'missing' / 'empty' / 'error'
const STATUS_ICONS = { ok: '✅', missing: '❌', empty: '❌', error: '⚠️' };
function statusOrder(st) { return st === 'ok' ? 0 : 1; }

// Hourly-only columns
const COLUMNS = [
  { key: '#',        label: '#',         sortKey: null },
  { key: 'ticker',   label: 'Ticker',    sortKey: 'ticker' },
  { key: 'name',     label: 'Name',      sortKey: 'name' },
  { key: 'sector',   label: 'Sector',    sortKey: 'sector' },
  { key: 'index',    label: 'Index',     sortKey: 'index_name' },
  { key: 'h_years',  label: 'Роки',      sortKey: null,             accent: 'hourly' },
  { key: 'h_from',   label: 'H From',    sortKey: 'h_from',         accent: 'hourly' },
  { key: 'h_to',     label: 'H To',      sortKey: 'h_to',           accent: 'hourly' },
  { key: 'h_bars',   label: 'H Bars',    sortKey: 'h_bars',         accent: 'hourly' },
  { key: 'h_status', label: 'Status',    sortKey: 'h_status_order', accent: 'hourly' },
  { key: 'size_mb',  label: 'Size (MB)', sortKey: 'size_mb' },
];

// Конвертація flat рядка в nested формат для TickerDetail
function flatToNested(r) {
  const toSub = (from, to, bars, sizeMb, status) => ({
    has_data:    status === 'ok',
    date_from:   from  || null,
    date_to:     to    || null,
    bars:        bars  || 0,
    file_size_kb: sizeMb ? sizeMb * 1024 : 0,
    status:      status === 'ok' ? 'full' : 'none',
  });
  return {
    ticker:    r.ticker,
    name:      r.name,
    sector:    r.sector,
    index:     r.index_name,
    hourly:    toSub(r.h_from,  r.h_to,  r.h_bars,  r.h_size_mb,  r.h_status),
    daily:     toSub(r.d_from,  r.d_to,  r.d_bars,  r.d_size_mb,  r.d_status),
    minute_5m: toSub(r.m5_from, r.m5_to, r.m5_bars, r.m5_size_mb, r.m5_status),
    minute_1m: toSub(r.m1_from, r.m1_to, r.m1_bars, r.m1_size_mb, r.m1_status),
  };
}

// Хелпер: знайти запис summary по data_type
function useSumEntry(summary, dtype) {
  return summary.find(s => s.data_type === dtype) || {};
}

// ── Картка summary ────────────────────────────────────────────────────────────
function SummaryCard({ label, dtype, color, icon, summary }) {
  const s = useSumEntry(summary, dtype);
  const total = (s.total || 0) + (s.missing || 0);
  return (
    <div style={{ ...S.statsCard, borderLeft: `4px solid ${color}` }}>
      <div style={{ ...S.statsTitle, color }}>{icon} {label}</div>
      <div style={S.statsRow}>
        <div style={S.statItem}>
          <div style={S.statLabel}>З даними</div>
          <div style={{ ...S.statValue, color: C.success }}>{(s.total || 0).toLocaleString()}</div>
          <div style={S.statSub}>{total ? ((s.total || 0) / total * 100).toFixed(1) : 0}% покриття</div>
        </div>
        <div style={S.statItem}>
          <div style={S.statLabel}>Без даних</div>
          <div style={{ ...S.statValue, color: C.danger }}>{(s.missing || 0).toLocaleString()}</div>
          <div style={S.statSub}>файли відсутні</div>
        </div>
        <div style={S.statItem}>
          <div style={S.statLabel}>Період</div>
          <div style={{ ...S.statValue, fontSize: 14, paddingTop: 3 }}>{s.period_from || '—'}</div>
          <div style={S.statSub}>→ {s.period_to || '—'}</div>
        </div>
        <div style={S.statItem}>
          <div style={S.statLabel}>Розмір</div>
          <div style={S.statValue}>{s.total_size_mb ? s.total_size_mb.toFixed(1) : '0'} MB</div>
          <div style={S.statSub}>parquet файлів</div>
        </div>
      </div>
    </div>
  );
}

const KNOWN_INDEXES = ['SP500', 'SP400', 'SP600'];

// 2019 показуємо навмисно: саме там проходить межа НАШОЇ закачки (у 2516 тікерів
// перший бар припадає на 2020-й, у 751 — на 2019-й), і без цього стовпця
// сховище виглядає глибшим, ніж воно є.
const COVERAGE_YEARS = [2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026];

// Короткі підписи причин під стовпчиком року; повний текст — у підказці при
// наведенні. Коди приходять із scripts/build_hourly_deep.py.
const REASON_SHORT = {
  deepened:         'докачано',
  seam_hole:        'символ звільнявся',
  other_company:    'інша компанія',
  newer_instrument: 'новий інструмент',
  price_gap:        'ціни не збіглись',
  thin_overlap:     'мало спільних барів',
  bad_bars:         'биті бари в джерелі',
  split_unadjusted: 'спліт не відпрацьовано',
  no_vendor:        'немає у 2-го джерела',
  nothing_older:    'давніших немає ніде',
  read_error:       'файл не читається',
  tail_changed:     'зрушило б наші бари',
  unknown:          'причина не з’ясована',
};

export default function DataTab() {
  const [summary,        setSummary]        = useState([]);
  const [tickers,        setTickers]        = useState([]);
  const [total,          setTotal]          = useState(0);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [needRefresh,    setNeedRefresh]    = useState(false);
  const [refreshing,     setRefreshing]     = useState(false);
  const [search,         setSearch]         = useState('');
  const [dataFilter,     setDataFilter]     = useState('all');
  const [indexFilter,    setIndexFilter]    = useState('all');
  const [sectorFilter,   setSectorFilter]   = useState('all');
  const [sortKey,        setSortKey]        = useState('ticker');
  const [sortDir,        setSortDir]        = useState(1);
  const [page,           setPage]           = useState(0);
  const [pageSize,       setPageSize]       = usePersistentPageSize('pg_datatab', 50);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [dlMsg,          setDlMsg]          = useState('');
  const [sectors,        setSectors]        = useState([]);
  const [marketStats,    setMarketStats]    = useState(null);
  const [hourlyProgress, setHourlyProgress] = useState(null);

  // Завантажуємо summary окремо (один раз)
  useEffect(() => {
    getDataSummary()
      .then(r => {
        if (r.data.cached) {
          setSummary(r.data.summary);
          setNeedRefresh(false);
        } else {
          setNeedRefresh(true);
        }
      })
      .catch(() => setNeedRefresh(true));
    getDataSectors().then(r => setSectors(r.data || [])).catch(() => {});
    getDataMarketStats().then(r => setMarketStats(r.data)).catch(() => {});
    getHourlyProgress().then(r => setHourlyProgress(r.data)).catch(() => {});
  }, []);

  // Маппінг фронтенд-ключа сортування → ключ для бекенду
  const SORT_COL_MAP = {
    h_status_order:  'h_status_order',
    d_status_order:  'd_status_order',
    m5_status_order: 'm5_status_order',
    m1_status_order: 'm1_status_order',
    size_mb:         'size_mb',
  };

  // Маппінг фільтрів → параметри API
  const apiParams = useMemo(() => {
    const p = { limit: pageSize, offset: page * pageSize };
    if (search)                p.search  = search;
    if (indexFilter !== 'all') p.index   = indexFilter;
    if (sectorFilter !== 'all') p.sector = sectorFilter;
    if (dataFilter === 'with') {
      p.data_type   = 'hourly';
      p.data_filter = 'with';
    } else if (dataFilter === 'without') {
      p.data_type   = 'hourly';
      p.data_filter = 'without';
    }
    // dataFilter === 'all' → не надсилаємо ні data_type, ні data_filter
    if (sortKey) {
      p.sort_col = SORT_COL_MAP[sortKey] || sortKey;
      p.sort_dir = sortDir === 1 ? 'asc' : 'desc';
    }
    return p;
  }, [search, dataFilter, indexFilter, sectorFilter, page, pageSize, sortKey, sortDir]);

  // Завантажуємо тікери при зміні фільтрів/сторінки
  useEffect(() => {
    setLoading(true);
    setError('');
    getDataTickers(apiParams)
      .then(r => {
        const d = r.data;
        if (!d.cached && d.total === 0) {
          setNeedRefresh(true);
        }
        setTickers(d.tickers || []);
        setTotal(d.total || 0);
      })
      .catch(() => setError('Не вдалося завантажити дані'))
      .finally(() => setLoading(false));
  }, [apiParams]);

  // Дані вже відсортовані сервером — просто використовуємо як є
  const sorted = tickers;

  const totalPages = Math.ceil(total / pageSize);

  function handleSort(col) {
    if (!col.sortKey) return;
    if (sortKey === col.sortKey) setSortDir(d => -d);
    else { setSortKey(col.sortKey); setSortDir(1); }
  }

  function sortArrow(col) {
    if (!col.sortKey || sortKey !== col.sortKey) return '';
    return sortDir === 1 ? ' ↑' : ' ↓';
  }

  const handleRefreshCache = useCallback(async () => {
    setRefreshing(true);
    try {
      await refreshDataCache();
      setDlMsg('✓ Кеш оновлюється... (~30 сек). Оновіть сторінку через хвилину.');
      setTimeout(() => {
        // Перезавантажуємо summary після оновлення
        getDataSummary().then(r => {
          if (r.data.cached) { setSummary(r.data.summary); setNeedRefresh(false); }
        });
        setDlMsg('');
      }, 35000);
    } catch {
      setDlMsg('✗ Не вдалося запустити оновлення кешу');
      setTimeout(() => setDlMsg(''), 4000);
    } finally {
      setRefreshing(false);
    }
  }, []);

  function renderCell(col, row, absIdx) {
    switch (col.key) {
      case '#': return <td key="#" style={{ ...S.tdNum, color: C.textSm, width: 40 }}>{absIdx}</td>;
      case 'ticker': return (
        <td key="ticker" style={{ ...S.td, fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}>
          {row.ticker}
        </td>
      );
      case 'name': return (
        <td key="name" style={{ ...S.td, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.name}
        </td>
      );
      case 'sector': return (
        <td key="sector" style={S.td}>
          <span style={S.badge(row.sector === 'Information Technology' ? 'green' : row.sector === 'Health Care' ? 'orange' : '')}>
            {row.sector || '—'}
          </span>
        </td>
      );
      case 'index': return (
        <td key="index" style={S.td}>
          <span style={S.badge(row.index_name === 'SP500' ? 'green' : row.index_name === 'SP400' ? 'orange' : '')}>
            {row.index_name || '—'}
          </span>
        </td>
      );
      // Hourly years coverage
      case 'h_years': {
        const yrs = Array.isArray(row.h_years) ? row.h_years : [];
        return (
          <td key="h_years" style={{ ...S.td, background: '#F8FBFF' }}>
            <div style={{ display: 'flex', gap: 3 }}>
              {COVERAGE_YEARS.map(y => (
                <span key={y} style={{
                  fontSize: 9, fontWeight: 700, padding: '1px 3px', borderRadius: 3,
                  background: yrs.includes(y) ? '#DCFCE7' : '#F1F5F9',
                  color: yrs.includes(y) ? '#15803D' : '#CBD5E1',
                }}>{y}</span>
              ))}
            </div>
          </td>
        );
      }
      // Hourly
      case 'h_from':   return <td key="h_from"   style={{ ...S.td,    background: '#F8FBFF' }}>{row.h_from  || '—'}</td>;
      case 'h_to':     return <td key="h_to"     style={{ ...S.td,    background: '#F8FBFF' }}>{row.h_to    || '—'}</td>;
      case 'h_bars':   return <td key="h_bars"   style={{ ...S.tdNum, background: '#F8FBFF' }}>{row.h_bars  ? row.h_bars.toLocaleString() : '—'}</td>;
      case 'h_status': return <td key="h_status" style={{ ...S.td, textAlign: 'center', fontSize: 16, background: '#F8FBFF' }}>{STATUS_ICONS[row.h_status] || '—'}</td>;
      case 'size_mb': {
        const mb = ((row.h_size_mb || 0) + (row.d_size_mb || 0) +
                    (row.m5_size_mb || 0) + (row.m1_size_mb || 0)).toFixed(2);
        return <td key="size_mb" style={S.tdNum}>{mb > 0 ? mb : '—'}</td>;
      }
      default: return <td key={col.key} style={S.td}>—</td>;
    }
  }

  // Показуємо TickerDetail при кліку на рядок
  if (selectedTicker) {
    return <TickerDetail ticker={selectedTicker} onBack={() => setSelectedTicker(null)} />;
  }

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* ── Header ───────────────────────────────────────────── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: C.textMd }}>📊 Годинні дані (data/hourly/)</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${C.border}`, borderRadius: 6, background: C.cardBg, color: C.textMd, opacity: refreshing ? 0.6 : 1 }}
              onClick={handleRefreshCache}
              disabled={refreshing}
              title="Сканувати parquet файли і оновити кеш в БД"
            >
              {refreshing ? '⟳ Оновлення...' : '🔄 Refresh Cache'}
            </button>
            {dlMsg && <span style={{ fontSize: 12, color: C.textMd }}>{dlMsg}</span>}
          </div>
        </div>

        {/* ── Банер якщо кеш порожній ──────────────────────────── */}
        {needRefresh && (
          <div style={S.alertBanner}>
            <span>⚠️ <strong>Кеш порожній.</strong> Натисніть «🔄 Refresh Cache» щоб просканувати файли і заповнити кеш.</span>
          </div>
        )}

        {/* ── Market overview stats ─────────────────────────────── */}
        {marketStats && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            {/* By index */}
            <div style={{ ...S.statsCard, borderLeft: '4px solid #6366F1' }}>
              <div style={{ ...S.statsTitle, color: '#4F46E5' }}>🏛️ По індексах</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {[['SP500', '#16A34A'], ['SP400', '#D97706'], ['SP600', '#DC2626']].map(([idx, color]) => {
                  const found = marketStats.by_index?.find(r => r.index_name === idx);
                  return (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: C.textMd, fontWeight: 600 }}>{idx}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 80, height: 6, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${marketStats.total ? Math.round((found?.count || 0) / marketStats.total * 100) : 0}%`, height: '100%', background: color, borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text, minWidth: 32, textAlign: 'right' }}>{(found?.count || 0)}</span>
                      </div>
                    </div>
                  );
                })}
                <div style={{ marginTop: 4, fontSize: 11, color: C.textSm }}>Всього: {marketStats.total} компаній · з даними: {marketStats.with_data}</div>
              </div>
            </div>

            {/* Market cap distribution */}
            <div style={{ ...S.statsCard, borderLeft: '4px solid #8B5CF6' }}>
              <div style={{ ...S.statsTitle, color: '#7C3AED' }}>💰 Капіталізація</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {[
                  ['Large cap', marketStats.large_cap_count, '>$10B', '#16A34A'],
                  ['Mid cap',   marketStats.mid_cap_count,   '$2-10B', '#2563EB'],
                  ['Small cap', marketStats.small_cap_count, '$300M-2B', '#D97706'],
                  ['Micro cap', marketStats.micro_cap_count, '<$300M', '#DC2626'],
                ].map(([label, count, range, color]) => {
                  const total = (marketStats.large_cap_count || 0) + (marketStats.mid_cap_count || 0) + (marketStats.small_cap_count || 0) + (marketStats.micro_cap_count || 0);
                  return (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: C.textMd }}>{label} <span style={{ color: C.textSm }}>{range}</span></span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 60, height: 5, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${total ? Math.round((count || 0) / total * 100) : 0}%`, height: '100%', background: color, borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text, minWidth: 28, textAlign: 'right' }}>{count || 0}</span>
                      </div>
                    </div>
                  );
                })}
                {marketStats.total_market_cap > 0 && (
                  <div style={{ marginTop: 4, fontSize: 11, color: C.textSm }}>
                    Сумарна: ${(marketStats.total_market_cap / 1e12).toFixed(1)}T · Медіана: ${(marketStats.median_market_cap / 1e9).toFixed(1)}B
                  </div>
                )}
              </div>
            </div>

            {/* Top sectors */}
            <div style={{ ...S.statsCard, borderLeft: '4px solid #EC4899' }}>
              <div style={{ ...S.statsTitle, color: '#DB2777' }}>🏭 Топ секторів</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {(marketStats.by_sector || []).slice(0, 6).map(({ sector, count }) => {
                  const maxCount = marketStats.by_sector?.[0]?.count || 1;
                  const shortSector = sector.replace('Information Technology', 'IT').replace('Communication Services', 'Comm.').replace('Consumer Discretionary', 'Cons.Disc.').replace('Consumer Staples', 'Cons.Staples').replace('Health Care', 'Health').replace('Financials', 'Fin.').replace('Real Estate', 'RE');
                  return (
                    <div key={sector} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 11, color: C.textMd, maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={sector}>{shortSector}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 60, height: 5, background: C.border, borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.round(count / maxCount * 100)}%`, height: '100%', background: '#EC4899', borderRadius: 3 }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text, minWidth: 28, textAlign: 'right' }}>{count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Summary card (hourly only) ────────────────────────── */}
        <div style={S.statsGrid}>
          <SummaryCard label="Hourly — data/hourly/" dtype="hourly" color="#2563EB" icon="📊" summary={summary} />
        </div>

        {/* ── Hourly Coverage by Year ─────────────────────────── */}
        {hourlyProgress && (
          <div style={{ ...S.statsCard, marginBottom: 16, borderLeft: '4px solid #2563EB' }}>
            <div style={{ ...S.statsTitle, color: '#2563EB' }}>📊 Покриття годинних даних по роках</div>
            <div style={{ fontSize: 12, color: C.textSm, marginBottom: 12, lineHeight: 1.5 }}>
              Сховище, з якого <strong>справді рахує рушій</strong>
              {hourlyProgress.store ? <> — <code>{String(hourlyProgress.store).replace('/app/', '')}</code></> : null}:
              котирування Interactive Brokers плюс перевірена передісторія другого джерела. Відсоток — від компаній, що <strong>торгувались того року</strong>,
              а не від сьогоднішнього списку: папір, який вийшов на біржу у 2025-му, не може мати барів за 2020-й
              і дірою не є.
              <br />
              Синє «готово в запасі» — передісторію вже викачано з другого джерела
              в <code>data/hourly_deep</code>; бракує її лише робочому сховищу, і
              рушій побачить її після перемикання каталогу.
              <br />
              Червоне — папір торгувався, а барів немає, і причини різні:
              {' '}<strong>не докачано</strong> — бари за той рік існують у другого постачальника, а в нас
              історія починається пізніше (саме тому 2019-й майже порожній: качати з IBKR почали з 2020-го).
              Якщо року немає в обох джерел — символ тоді просто не торгувався, і в «немає» це не рахується;
              {' '}<strong>діра в ряді</strong> — рік випав усередині наявної історії, це справжня втрата;
              {' '}<strong>ряд обірвано</strong> — папір перестав оновлюватись раніше (переважно делістинг
              або перейменування, як BK&nbsp;→&nbsp;BNY).
            </div>
            <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* Year bars */}
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                {COVERAGE_YEARS.map(y => {
                  const cell = hourlyProgress.by_year?.[String(y)] || {};
                  // Back-compat: старий ендпоінт віддавав число, новий — об'єкт.
                  const isObj = typeof cell === 'object';
                  const count = isObj ? (cell.have || 0) : (cell || 0);
                  const existed = isObj ? (cell.existed || 0) : (hourlyProgress.with_any || 1);
                  const gaps = isObj ? (cell.gaps || 0) : null;
                  const pct = existed ? Math.min(100, Math.round(count / existed * 100)) : 0;
                  return (
                    <div key={y} style={{ minWidth: 104 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{y}</span>
                        <span style={{ fontSize: 12, color: C.textMd }}>{pct}%</span>
                      </div>
                      <div style={{ width: '100%', height: 8, background: C.border, borderRadius: 4, overflow: 'hidden', marginBottom: 3 }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: count > 0 ? '#2563EB' : C.border, borderRadius: 4 }} />
                      </div>
                      <div style={{ fontSize: 11, color: C.textSm }}>
                        {count.toLocaleString()}{existed ? ` з ${existed.toLocaleString()}` : ''}
                      </div>
                      {gaps !== null && (
                        gaps > 0 ? (
                          // Голе «немає: N» одразу викликає питання «а чому?»,
                          // тож причина стоїть поруч із числом, а не в довідці.
                          <div
                            style={{ fontSize: 11, color: C.danger, lineHeight: 1.35 }}
                            title={(cell.why || []).map(w => `${w.text}: ${w.n}`).join('\n')
                                 || `рік випав усередині ряду: ${cell.gaps_hole || 0}\n`
                                    + `ряд обірвався раніше: ${cell.gaps_stale || 0}`}
                          >
                            немає: {(cell.gaps_missing != null ? cell.gaps_missing : gaps).toLocaleString()}
                            <div style={{ color: C.textSm }}>
                              {/* Причини всі до одної: обрізане «ще N причин» лишає
                                  саме те питання, заради якого список і робився. */}
                              {(cell.why || []).map(w => (
                                <span key={w.code}>{REASON_SHORT[w.code] || w.code}: {w.n.toLocaleString()}<br /></span>
                              ))}
                              {(cell.gaps_hole || 0) > 0 && <>діра в ряді: {(cell.gaps_hole).toLocaleString()}<br /></>}
                              {(cell.gaps_stale || 0) > 0 && <>ряд обірвано: {(cell.gaps_stale).toLocaleString()}<br /></>}
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, color: C.success }}>повні</div>
                        )
                      )}
                      {(cell.ready_deep || 0) > 0 && (
                        // Окремо і синім: ці бари вже викачані й лежать у
                        // data/hourly_deep — бракує їх лише робочому сховищу,
                        // тож у переліку причин «немає» їм не місце.
                        <div style={{ fontSize: 11, color: '#2563EB' }}
                             title="Передісторія вже викачана в глибоке сховище (data/hourly_deep). Рушій почне її бачити після перемикання HOURLY_CACHE_DIR.">
                          готово в запасі: {(cell.ready_deep).toLocaleString()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Summary numbers */}
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginLeft: 'auto' }}>
                {[
                  ['Всього тікерів', hourlyProgress.total, C.text],
                  ['З даними (будь-який рік)', hourlyProgress.with_any, C.success],
                  ['Без даних', hourlyProgress.no_data, C.danger],
                  ['2020–2025 ✓', hourlyProgress.fully_covered, '#7C3AED'],
                ].map(([lbl, val, color]) => (
                  <div key={lbl} style={S.statItem}>
                    <div style={S.statLabel}>{lbl}</div>
                    <div style={{ ...S.statValue, color, fontSize: 22 }}>{(val || 0).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Filters ─────────────────────────────────────────── */}
        <div style={S.filterBar}>
          <input
            style={S.input}
            placeholder="🔍 Пошук за тікером або назвою…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
          <span style={S.filterLabel}>Дані:</span>
          <select style={S.select} value={dataFilter} onChange={e => { setDataFilter(e.target.value); setPage(0); }}>
            <option value="all">Всі</option>
            <option value="with">З годинними даними</option>
            <option value="without">Без годинних даних</option>
          </select>
          <span style={S.filterLabel}>Index:</span>
          <select style={S.select} value={indexFilter} onChange={e => { setIndexFilter(e.target.value); setPage(0); }}>
            <option value="all">Всі</option>
            {KNOWN_INDEXES.map(ix => (
              <option key={ix} value={ix}>{ix}</option>
            ))}
          </select>
          <span style={S.filterLabel}>Сектор:</span>
          <select style={{ ...S.select, maxWidth: 200 }} value={sectorFilter} onChange={e => { setSectorFilter(e.target.value); setPage(0); }}>
            <option value="all">Всі сектори</option>
            {sectors.map(s => (
              <option key={s} value={s}>{s.replace('Information Technology', 'IT').replace('Communication Services', 'Comm.').replace('Consumer Discretionary', 'Cons.Disc.').replace('Consumer Staples', 'Cons.Staples')}</option>
            ))}
          </select>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: C.textSm }}>
            {total.toLocaleString()} результатів
          </span>
        </div>

        {/* ── Table ───────────────────────────────────────────── */}
        <div style={S.card}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.textMd }}>
              Завантаження…
            </div>
          ) : error ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.danger }}>{error}</div>
          ) : (
            <>
              <div style={S.tableWrap}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      {COLUMNS.map(col => (
                        <th
                          key={col.key}
                          style={S.th(!!col.sortKey, col.accent)}
                          onClick={() => handleSort(col)}
                          title={col.sortKey ? 'Сортувати' : ''}
                        >
                          {col.label}{sortArrow(col)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((row, i) => {
                      const absIdx  = page * pageSize + i + 1;
                      const hasData = row.h_status === 'ok';
                      return (
                        <tr
                          key={row.ticker}
                          style={{ ...(i % 2 === 1 ? S.rowAlt : {}), cursor: hasData ? 'pointer' : 'default' }}
                          onClick={() => hasData && setSelectedTicker(flatToNested(row))}
                          title={hasData ? `Відкрити графік ${row.ticker}` : ''}
                        >
                          {COLUMNS.map(col => renderCell(col, row, absIdx))}
                        </tr>
                      );
                    })}
                    {sorted.length === 0 && (
                      <tr>
                        <td colSpan={COLUMNS.length} style={{ ...S.td, textAlign: 'center', color: C.textSm, padding: 32 }}>
                          {needRefresh ? 'Кеш порожній — натисніть «🔄 Refresh Cache»' : 'Нічого не знайдено'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* ── Pagination ── */}
              <div style={{ ...S.pagRow, flexWrap: 'wrap', gap: 8 }}>
                {totalPages > 1 && (
                  <>
                    <span style={{ fontSize: 12, color: C.textMd }}>
                      Сторінка {page + 1} з {totalPages}
                      {' · '}
                      рядки {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)} з {total}
                    </span>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button style={S.btnPage(false)} disabled={page === 0}              onClick={() => setPage(0)}>«</button>
                      <button style={S.btnPage(false)} disabled={page === 0}              onClick={() => setPage(p => p - 1)}>‹</button>
                      {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                        const start = Math.max(0, Math.min(page - 3, totalPages - 7));
                        const p = start + i;
                        return (
                          <button key={p} style={S.btnPage(p === page)} onClick={() => setPage(p)}>
                            {p + 1}
                          </button>
                        );
                      })}
                      <button style={S.btnPage(false)} disabled={page === totalPages - 1} onClick={() => setPage(p => p + 1)}>›</button>
                      <button style={S.btnPage(false)} disabled={page === totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                  <span style={{ fontSize: 12, color: C.textSm }}>Рядків:</span>
                  {PAGE_SIZE_OPTIONS.map(opt => (
                    <button
                      key={opt}
                      onClick={() => { setPageSize(opt); setPage(0); }}
                      style={{
                        padding: '2px 8px', borderRadius: 5, cursor: 'pointer', fontSize: 12,
                        border: `1px solid ${pageSize === opt ? C.primary : C.border}`,
                        background: pageSize === opt ? C.primary : 'transparent',
                        color:      pageSize === opt ? '#fff' : C.textMd,
                        fontWeight: pageSize === opt ? 700 : 400,
                      }}
                    >{opt}</button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* ── Legend ──────────────────────────────────────────── */}
        <div style={{ marginTop: 12, fontSize: 12, color: C.textSm, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <span>📊 <strong>Hourly</strong> — годинні котирування IBKR (data/hourly/)</span>
          <span>✅ Є дані &nbsp;|&nbsp; ❌ Немає даних</span>
          <span>🔄 Сортування — серверне, по всіх сторінках</span>
          <span>Колонка <strong>Роки</strong> = роки, у яких бари справді є</span>
        </div>

      </div>
    </div>
  );
}
